import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { AddIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Paragraph,
	SettingsEmptyState,
	SettingsSection,
	SettingsTable,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import LearningSubjectEdit from './LearningSubjectEdit';

const AnchorElement = styled(Link)`
	color: ${tokens.brand.text};
	text-decoration: underline;

	${Paragraph} {
		color: ${tokens.brand.text};
	}
`;

export default function LearningSubjects() {
	const { t } = useTranslation();

	const [adding, setAdding] = useState(false);
	const [showArchived, setShowArchived] = useState(false);

	const queries = {
		active: graphql.useLearningSubjectsQuery({ variables: { first: 1000 } }),
		archive: graphql.useLearningSubjectsLazyQuery({ variables: { isArchived: true, first: 1000 } }),
	};

	const learningSubjects = queries.active.data?.learningSubjects;
	const archivedLearningSubjects = queries.archive[1]?.data?.learningSubjects;

	const onRefresh = async () => {
		await queries.active.refetch();
		await queries.archive[1]?.refetch?.();
		setAdding(false);
	};

	return (
		<>
			<SettingsSection first>
				<SettingsTable
					title={t('common.settings.learning.learningSubjects.title')}
					description={t('common.settings.learning.learningSubjects.description')}
					toolbar={
						<Button
							icon={<AddIcon />}
							title={t('common.add')}
							onClick={() => setAdding(true)}
							type="secondary"
						/>
					}
				>
					{learningSubjects ? (
						learningSubjects.edges.length > 0 || adding ? (
							<>
								{learningSubjects.edges.map(({ node }) => (
									<LearningSubjectEdit
										key={node.id}
										learningSubject={node as graphql.LearningSubject}
										onRefresh={onRefresh}
									/>
								))}
								{adding && <LearningSubjectEdit onRefresh={onRefresh} />}
							</>
						) : (
							<SettingsEmptyState>
								{t('common.settings.learning.learningSubjects.empty')}
							</SettingsEmptyState>
						)
					) : (
						<Flex my="22px" mx={3}>
							<Spinner />
						</Flex>
					)}
				</SettingsTable>
			</SettingsSection>

			{showArchived ? (
				<SettingsSection>
					<SettingsTable
						divided
						title={t('common.settings.learning.learningSubjects.archived.title')}
						description={t('common.settings.learning.learningSubjects.archived.description')}
					>
						{archivedLearningSubjects ? (
							archivedLearningSubjects.edges.length > 0 || adding ? (
								<>
									{archivedLearningSubjects.edges.map(({ node }) => (
										<LearningSubjectEdit
											key={node.id}
											learningSubject={node as graphql.LearningSubject}
											onRefresh={onRefresh}
										/>
									))}
									{adding && <LearningSubjectEdit onRefresh={onRefresh} />}
								</>
							) : (
								<SettingsEmptyState>
									{t('common.settings.learning.learningSubjects.archived.empty')}
								</SettingsEmptyState>
							)
						) : (
							<Spinner />
						)}
					</SettingsTable>
				</SettingsSection>
			) : (
				<Flex px={3}>
					<AnchorElement
						to="/settings/learning/subjects"
						onClick={() => {
							setShowArchived(true);
							queries.archive[0]();
						}}
					>
						<Paragraph>
							{t('common.settings.learning.learningSubjects.archived.button.title')}
						</Paragraph>
					</AnchorElement>
				</Flex>
			)}
		</>
	);
}
